import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Copy, Check, QrCode, Loader2, ArrowLeft, MapPin, Clock, AlertTriangle, Bitcoin, CreditCard, FileText, ExternalLink, RefreshCw, Coins } from 'lucide-react';
import { toast } from 'sonner';
import api, { API_URL } from '../lib/api';

const MONTHLY_RATE = 0.0249;

function calcInstallments(total, policy) {
  const options = [];
  if (policy === 'no_installments') {
    options.push({ n: 1, value: total, total, interest: 0, free: true });
    return options;
  }
  const maxFree = policy === 'seller_absorbs_3x' ? 3 : 1;
  for (let n = 1; n <= 12; n++) {
    if (n <= maxFree) {
      options.push({ n, value: total / n, total, interest: 0, free: true });
    } else {
      const r = MONTHLY_RATE;
      const value = total * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
      const t = value * n;
      options.push({ n, value, total: t, interest: t - total, free: false });
    }
  }
  return options;
}

function formatBRL(v) { return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function CheckoutPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [checking, setChecking] = useState(false);

  // Card form state
  const [cardForm, setCardForm] = useState({
    number: '', name: '', expMonth: '', expYear: '', cvv: '', cpf: ''
  });
  const [installments, setInstallments] = useState(1);
  const [mpReady, setMpReady] = useState(false);
  const mpRef = useRef(null);

  // Boleto form
  const [boletoForm, setBoletoForm] = useState({
    first_name: '', last_name: '', cpf: '', zip_code: '',
    street: '', number: '', neighborhood: '', city: '', state: ''
  });

  const pollingRef = useRef(null);
  const calculateFee = (price) => 0;

  // Load MP SDK
  useEffect(() => {
    if (document.getElementById('mp-sdk-script')) {
      if (window.MercadoPago) {
        initMP();
      }
      return;
    }
    const script = document.createElement('script');
    script.id = 'mp-sdk-script';
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.async = true;
    script.onload = () => initMP();
    document.head.appendChild(script);
  }, []);

  const initMP = useCallback(async () => {
    try {
      const { data } = await api.get('/config/mp-public-key');
      if (data.public_key && window.MercadoPago) {
        mpRef.current = new window.MercadoPago(data.public_key);
        setMpReady(true);
      }
    } catch (err) {
      console.warn('MP SDK init failed:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get(`/products/${productId}`)
      .then(r => { setProduct(r.data.product); setLoading(false); })
      .catch(() => { navigate('/products'); });
  }, [productId, user, navigate]);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const startPolling = (txId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/escrow/check-payment/${txId}`);
        if (['approved', 'paid'].includes(data.payment_status) || data.transaction?.status === 'paid') {
          clearInterval(pollingRef.current);
          setTransaction(data.transaction);
          toast.success('Pagamento confirmado!');
        }
      } catch { /* silent */ }
    }, 8000);
  };

  const tokenizeCard = async () => {
    if (!mpRef.current) throw new Error('SDK de pagamento não carregado');
    const { number, name, expMonth, expYear, cvv, cpf } = cardForm;
    if (!number || !name || !expMonth || !expYear || !cvv) throw new Error('Preencha todos os campos do cartão');

    // Get payment method from BIN
    const bin = number.replace(/\s/g, '').substring(0, 6);
    let paymentMethodId = 'visa';
    try {
      const methods = await mpRef.current.getPaymentMethods({ bin });
      if (methods.results?.length > 0) paymentMethodId = methods.results[0].id;
    } catch { /* use default */ }

    const tokenResult = await mpRef.current.createCardToken({
      cardNumber: number.replace(/\s/g, ''),
      cardholderName: name,
      cardExpirationMonth: expMonth,
      cardExpirationYear: expYear.length === 2 ? `20${expYear}` : expYear,
      securityCode: cvv,
      identificationType: 'CPF',
      identificationNumber: cpf.replace(/\D/g, ''),
    });

    return { token: tokenResult.id, paymentMethodId };
  };

  const handleCheckout = async () => {
    setPaying(true);
    setError('');
    try {
      const payload = {
        product_id: productId,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      };

      if (paymentMethod === 'card') {
        if (mpReady && cardForm.number) {
          try {
            const { token, paymentMethodId } = await tokenizeCard();
            payload.card_token = token;
            payload.card_payment_method_id = paymentMethodId;
            payload.card_installments = installments;
            payload.card_payer_cpf = cardForm.cpf.replace(/\D/g, '');
          } catch (tokenErr) {
            // If tokenization fails, will fall back to Stripe on backend
            console.warn('Tokenization failed, using Stripe fallback:', tokenErr);
            toast.info('Processando via Stripe...');
          }
        }
      }

      if (paymentMethod === 'boleto') {
        payload.payer_first_name = boletoForm.first_name;
        payload.payer_last_name = boletoForm.last_name;
        payload.payer_cpf = boletoForm.cpf.replace(/\D/g, '');
        payload.payer_email = user?.email || '';
        payload.payer_zip_code = boletoForm.zip_code.replace(/\D/g, '');
        payload.payer_street = boletoForm.street;
        payload.payer_number = boletoForm.number;
        payload.payer_neighborhood = boletoForm.neighborhood;
        payload.payer_city = boletoForm.city;
        payload.payer_state = boletoForm.state;
      }

      const { data } = await api.post('/escrow/checkout', payload);
      setTransaction(data.transaction);
      toast.success(data.message);

      // Stripe fallback: redirect
      if (data.transaction.card_fallback === 'stripe' && data.transaction.oasis_checkout_url) {
        window.open(data.transaction.oasis_checkout_url, '_blank');
      }

      if (data.transaction._id && data.transaction.status === 'awaiting_payment') {
        startPolling(data.transaction._id);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao iniciar pagamento';
      setError(msg);
      toast.error(msg);
    } finally {
      setPaying(false);
    }
  };

  const copyPix = () => {
    const code = transaction?.oasis_qr_code_copy_paste || transaction?.oasis_qr_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCancel = async () => {
    if (!transaction) return;
    try {
      if (pollingRef.current) clearInterval(pollingRef.current);
      await api.post(`/escrow/cancel/${transaction._id}`);
      toast.success('Compra cancelada');
      setTransaction(null);
      setError('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const checkPaymentManually = async () => {
    if (!transaction) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/escrow/check-payment/${transaction._id}`);
      if (data.transaction?.status === 'paid') {
        setTransaction(data.transaction);
        toast.success('Pagamento confirmado!');
        if (pollingRef.current) clearInterval(pollingRef.current);
      } else {
        toast.info('Pagamento ainda não confirmado. Aguarde...');
      }
    } catch { toast.error('Erro ao verificar pagamento'); }
    finally { setChecking(false); }
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    return img.startsWith('http') ? img : `${API_URL}/api/files/${img}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const price = Number(product?.price || 0);
  const fee = calculateFee(price);
  const total = price + fee;
  const policy = product?.installment_policy || 'buyer_pays_all';
  const installmentOptions = calcInstallments(total, policy);

  const paymentMethods = [
    { id: 'credits', label: 'Créditos VDG', desc: `Saldo: R$ ${(user?.credits || 0).toFixed(2)}`, icon: Coins, color: 'text-emerald-600' },
    { id: 'pix', label: 'PIX', desc: 'OASIS', icon: QrCode, color: 'text-teal-600' },
    { id: 'card', label: 'Cartão', desc: 'Crédito/Débito', icon: CreditCard, color: 'text-blue-600' },
    { id: 'boleto', label: 'Boleto', desc: 'OASIS', icon: FileText, color: 'text-orange-600' },
    { id: 'crypto', label: 'Cripto', desc: 'OASIS Exchange', icon: Bitcoin, color: 'text-amber-500' },
  ];

  const selectedInstallment = installmentOptions.find(o => o.n === installments) || installmentOptions[0];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="checkout-page">
      <Link to={`/products/${productId}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Voltar ao produto
      </Link>

      {!transaction ? (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>Finalizar Compra</h1>

          {/* Product Summary */}
          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="order-summary">
            <div className="flex gap-4">
              {product?.images?.[0] && (
                <img src={getImageUrl(product.images[0])} alt={product.title} className="w-24 h-24 rounded-xl object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate">{product?.title}</h2>
                {(product?.location || product?.city) && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" /> {product.location || `${product.city}, ${product.state}`}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-1">{product?.category}</p>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-3" data-testid="payment-method-selection">
            <h3 className="font-semibold text-sm">Forma de pagamento</h3>
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map(m => (
                <button key={m.id} onClick={() => { setPaymentMethod(m.id); setInstallments(1); }}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${paymentMethod === m.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}
                  data-testid={`select-${m.id}`}
                >
                  <div className={`p-2 rounded-lg ${paymentMethod === m.id ? 'bg-primary/10' : 'bg-muted'}`}>
                    <m.icon className={`h-5 w-5 ${paymentMethod === m.id ? 'text-primary' : m.color}`} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-xs text-muted-foreground">{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Card Form */}
          {paymentMethod === 'card' && (
            <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="card-form">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-blue-600" /> Dados do Cartão
              </h3>
              <div>
                <Input placeholder="Número do cartão" value={cardForm.number}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').substring(0, 16);
                    const formatted = v.replace(/(\d{4})(?=\d)/g, '$1 ');
                    setCardForm(f => ({ ...f, number: formatted }));
                  }}
                  data-testid="card-number" />
              </div>
              <div>
                <Input placeholder="Nome impresso no cartão" value={cardForm.name}
                  onChange={e => setCardForm(f => ({ ...f, name: e.target.value.toUpperCase() }))}
                  data-testid="card-name" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Mês (MM)" maxLength={2} value={cardForm.expMonth}
                  onChange={e => setCardForm(f => ({ ...f, expMonth: e.target.value.replace(/\D/g, '').substring(0, 2) }))}
                  data-testid="card-exp-month" />
                <Input placeholder="Ano (AA)" maxLength={2} value={cardForm.expYear}
                  onChange={e => setCardForm(f => ({ ...f, expYear: e.target.value.replace(/\D/g, '').substring(0, 2) }))}
                  data-testid="card-exp-year" />
                <Input placeholder="CVV" maxLength={4} type="password" value={cardForm.cvv}
                  onChange={e => setCardForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) }))}
                  data-testid="card-cvv" />
              </div>
              <div>
                <Input placeholder="CPF do titular" value={cardForm.cpf}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').substring(0, 11);
                    let formatted = v;
                    if (v.length > 9) formatted = `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9)}`;
                    else if (v.length > 6) formatted = `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6)}`;
                    else if (v.length > 3) formatted = `${v.slice(0,3)}.${v.slice(3)}`;
                    setCardForm(f => ({ ...f, cpf: formatted }));
                  }}
                  data-testid="card-cpf" />
              </div>

              {/* Installment Selector */}
              <div className="space-y-2" data-testid="installment-selector">
                <label className="text-sm font-medium">Parcelas</label>
                <Select value={String(installments)} onValueChange={v => setInstallments(Number(v))}>
                  <SelectTrigger data-testid="installment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {installmentOptions.map(opt => (
                      <SelectItem key={opt.n} value={String(opt.n)}>
                        {opt.n}x de R$ {formatBRL(opt.value)}
                        {opt.free ? ' (sem juros)' : ` (total R$ ${formatBRL(opt.total)})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!selectedInstallment.free && (
                  <p className="text-xs text-muted-foreground">
                    Total com juros: <span className="font-medium text-foreground">R$ {formatBRL(selectedInstallment.total)}</span>
                    {' '}(+R$ {formatBRL(selectedInstallment.interest)})
                  </p>
                )}
                {policy === 'seller_absorbs_3x' && (
                  <p className="text-xs text-emerald-600 font-medium">
                    Este vendedor oferece até 3x sem juros!
                  </p>
                )}
              </div>

              {!mpReady && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Se o pagamento via OASIS falhar, será redirecionado para o Stripe.
                </p>
              )}
            </div>
          )}

          {/* Boleto Form */}
          {paymentMethod === 'boleto' && (
            <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="boleto-form">
              <h3 className="font-semibold text-sm">Dados do pagador (Boleto)</h3>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Nome" value={boletoForm.first_name} onChange={e => setBoletoForm(f => ({...f, first_name: e.target.value}))} data-testid="boleto-first-name" />
                <Input placeholder="Sobrenome" value={boletoForm.last_name} onChange={e => setBoletoForm(f => ({...f, last_name: e.target.value}))} data-testid="boleto-last-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="CPF (apenas números)" value={boletoForm.cpf} onChange={e => setBoletoForm(f => ({...f, cpf: e.target.value}))} data-testid="boleto-cpf" />
                <Input placeholder="CEP" value={boletoForm.zip_code} onChange={e => setBoletoForm(f => ({...f, zip_code: e.target.value}))} data-testid="boleto-zip" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input className="col-span-2" placeholder="Rua" value={boletoForm.street} onChange={e => setBoletoForm(f => ({...f, street: e.target.value}))} data-testid="boleto-street" />
                <Input placeholder="Nº" value={boletoForm.number} onChange={e => setBoletoForm(f => ({...f, number: e.target.value}))} data-testid="boleto-number" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Bairro" value={boletoForm.neighborhood} onChange={e => setBoletoForm(f => ({...f, neighborhood: e.target.value}))} data-testid="boleto-neighborhood" />
                <Input placeholder="Cidade" value={boletoForm.city} onChange={e => setBoletoForm(f => ({...f, city: e.target.value}))} data-testid="boleto-city" />
                <Input placeholder="UF" maxLength={2} value={boletoForm.state} onChange={e => setBoletoForm(f => ({...f, state: e.target.value.toUpperCase()}))} data-testid="boleto-state" />
              </div>
            </div>
          )}

          {/* Credits Info */}
          {paymentMethod === 'credits' && (
            <div className="p-5 rounded-2xl border border-border bg-card space-y-3" data-testid="credits-form">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Coins className="h-4 w-4 text-emerald-600" /> Pagar com Créditos VDG
              </h3>
              <div className="flex justify-between items-center p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                <span className="text-sm text-emerald-800">Seu saldo</span>
                <span className="text-lg font-bold text-emerald-700">R$ {(user?.credits || 0).toFixed(2)}</span>
              </div>
              {(user?.credits || 0) < total ? (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Saldo insuficiente</p>
                    <p className="text-xs text-red-600 mt-0.5">Você precisa de R$ {total.toFixed(2)} mas tem apenas R$ {(user?.credits || 0).toFixed(2)}. Adicione créditos antes de continuar.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-emerald-700">Saldo suficiente! O pagamento será processado instantaneamente.</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Após o pagamento: R$ {((user?.credits || 0) - total).toFixed(2)} restante</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Breakdown */}
          <div className="p-5 rounded-2xl border border-border bg-card space-y-3" data-testid="price-breakdown">
            <h3 className="font-semibold text-sm">Resumo do pagamento</h3>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Preço do produto</span>
              <span>R$ {formatBRL(price)}</span>
            </div>
            {fee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Taxa da plataforma</span>
                <span>R$ {formatBRL(fee)}</span>
              </div>
            )}
            {paymentMethod === 'card' && !selectedInstallment.free && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Juros ({installments}x)</span>
                <span className="text-amber-600">+ R$ {formatBRL(selectedInstallment.interest)}</span>
              </div>
            )}
            <div className="border-t border-border pt-3 flex justify-between font-bold">
              <span>Total</span>
              <span className="text-primary text-lg">R$ {formatBRL(paymentMethod === 'card' ? selectedInstallment.total : total)}</span>
            </div>
            {paymentMethod === 'card' && installments > 1 && (
              <p className="text-xs text-muted-foreground text-right">
                {installments}x de R$ {formatBRL(selectedInstallment.value)}
              </p>
            )}
          </div>

          {/* Security Info */}
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="escrow-info">
            <div className="flex gap-3">
              <Shield className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Pagamento Seguro com Escrow</p>
                <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                  Seu pagamento fica sob custódia da plataforma até você confirmar o recebimento do produto.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex items-center gap-2" data-testid="checkout-error">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <Button onClick={handleCheckout} disabled={paying || (paymentMethod === 'credits' && (user?.credits || 0) < total)} className="w-full rounded-xl h-12 text-base gap-2" data-testid="pay-btn">
            {paying ? <Loader2 className="h-5 w-5 animate-spin" /> :
              paymentMethod === 'pix' ? <QrCode className="h-5 w-5" /> :
              paymentMethod === 'card' ? <CreditCard className="h-5 w-5" /> :
              paymentMethod === 'boleto' ? <FileText className="h-5 w-5" /> :
              <Bitcoin className="h-5 w-5" />}
            {paymentMethod === 'pix' ? `Pagar com PIX — R$ ${formatBRL(total)}` :
             paymentMethod === 'card' ? `Pagar ${installments}x de R$ ${formatBRL(selectedInstallment.value)}` :
             paymentMethod === 'boleto' ? `Gerar Boleto — R$ ${formatBRL(total)}` :
             `Pagar com Cripto — R$ ${formatBRL(total)}`}
          </Button>
        </div>
      ) : (
        <PaymentResult
          transaction={transaction}
          onCancel={handleCancel}
          onCheck={checkPaymentManually}
          checking={checking}
          onCopy={copyPix}
          copied={copied}
          navigate={navigate}
        />
      )}
    </div>
  );
}

function PaymentResult({ transaction, onCancel, onCheck, checking, onCopy, copied, navigate }) {
  const tx = transaction;

  if (tx.status === 'paid') {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>Pagamento Confirmado!</h1>
        <div className="p-6 rounded-2xl border border-emerald-200 bg-emerald-50 text-center space-y-3" data-testid="payment-confirmed">
          <Check className="h-12 w-12 text-emerald-600 mx-auto" />
          <h2 className="font-semibold text-lg">Pagamento Confirmado!</h2>
          <p className="text-sm text-emerald-700">O vendedor foi notificado e enviará o produto em breve.</p>
          {tx.card_installments > 1 && <p className="text-xs text-emerald-600">{tx.card_installments}x no cartão</p>}
          <Button onClick={() => navigate('/dashboard?tab=purchases')} className="rounded-xl mt-2">Acompanhar no Dashboard</Button>
        </div>
      </div>
    );
  }

  const methodConfig = {
    pix: { title: 'Pagamento PIX', color: 'amber', icon: Clock },
    card: { title: tx.card_fallback === 'stripe' ? 'Pagamento com Cartão (Stripe)' : 'Pagamento com Cartão', color: 'blue', icon: CreditCard },
    boleto: { title: 'Boleto Gerado', color: 'orange', icon: FileText },
    crypto: { title: 'Pagamento Cripto', color: 'amber', icon: Clock },
  };
  const cfg = methodConfig[tx.payment_method] || methodConfig.pix;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>{cfg.title}</h1>
      <div className="p-6 rounded-2xl border border-border bg-card text-center space-y-5" data-testid={`${tx.payment_method}-payment`}>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-${cfg.color}-50 text-${cfg.color}-700 text-sm font-medium`}>
          <cfg.icon className="h-4 w-4" /> Aguardando pagamento
        </div>

        {/* PIX QR */}
        {tx.payment_method === 'pix' && (
          <>
            {tx.oasis_qr_code_base64 && (
              <div className="flex justify-center">
                <img src={`data:image/png;base64,${tx.oasis_qr_code_base64}`} alt="QR Code PIX"
                  className="w-56 h-56 rounded-xl border border-border" data-testid="pix-qr-code" />
              </div>
            )}
            {tx.oasis_qr_code && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Ou copie o código PIX:</p>
                <div className="flex items-center gap-2 bg-muted rounded-xl p-3">
                  <code className="flex-1 text-xs break-all text-left">{(tx.oasis_qr_code_copy_paste || tx.oasis_qr_code).substring(0, 80)}...</code>
                  <Button variant="outline" size="sm" onClick={onCopy} className="shrink-0 gap-1.5" data-testid="copy-pix-btn">
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </Button>
                </div>
              </div>
            )}
            {tx.oasis_ticket_url && (
              <a href={tx.oasis_ticket_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                <ExternalLink className="h-3.5 w-3.5" /> Abrir no OASIS
              </a>
            )}
          </>
        )}

        {/* Card / Stripe redirect */}
        {tx.payment_method === 'card' && tx.card_fallback === 'stripe' && (
          <div className="p-4 rounded-xl bg-muted space-y-3">
            <CreditCard className="h-12 w-12 text-blue-500 mx-auto" />
            <p className="text-sm font-medium">Complete o pagamento no Stripe</p>
            <p className="text-xs text-muted-foreground">Uma janela foi aberta. Após concluir, clique em "Verificar pagamento".</p>
            {tx.oasis_checkout_url && (
              <Button variant="outline" size="sm" className="gap-1.5"
                onClick={() => window.open(tx.oasis_checkout_url, '_blank')} data-testid="reopen-stripe">
                <ExternalLink className="h-3.5 w-3.5" /> Reabrir página de pagamento
              </Button>
            )}
          </div>
        )}

        {/* Card MP (approved will show paid state above, this is for pending) */}
        {tx.payment_method === 'card' && !tx.card_fallback && (
          <div className="p-4 rounded-xl bg-muted space-y-3">
            <CreditCard className="h-12 w-12 text-blue-500 mx-auto" />
            <p className="text-sm font-medium">Processando pagamento...</p>
            <p className="text-xs text-muted-foreground">Seu pagamento está sendo processado pela OASIS.</p>
          </div>
        )}

        {/* Boleto */}
        {tx.payment_method === 'boleto' && (
          <div className="p-4 rounded-xl bg-muted space-y-3">
            <FileText className="h-12 w-12 text-orange-500 mx-auto" />
            <p className="text-sm font-medium">Boleto bancário gerado</p>
            <p className="text-xs text-muted-foreground">Pague em qualquer banco, lotérica ou app bancário. Prazo: até 3 dias úteis.</p>
            {(tx.boleto_url || tx.oasis_ticket_url) && (
              <Button variant="outline" size="sm" className="gap-1.5"
                onClick={() => window.open(tx.boleto_url || tx.oasis_ticket_url, '_blank')} data-testid="open-boleto">
                <ExternalLink className="h-3.5 w-3.5" /> Abrir Boleto
              </Button>
            )}
          </div>
        )}

        {/* Crypto */}
        {tx.payment_method === 'crypto' && (
          <div className="p-4 rounded-xl bg-muted">
            <Bitcoin className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <p className="text-sm font-medium">Valor: R$ {formatBRL(Number(tx.total_charged))}</p>
            <p className="text-xs text-muted-foreground mt-2">Realize o pagamento pela OASIS Exchange. O admin confirmará.</p>
          </div>
        )}

        <div className="text-sm text-muted-foreground">
          <p>Valor: <strong className="text-foreground">R$ {formatBRL(Number(tx.total_charged))}</strong></p>
        </div>

        <PaymentActions onCancel={onCancel} onCheck={onCheck} checking={checking} onDashboard={() => navigate('/dashboard?tab=purchases')} />
      </div>
    </div>
  );
}

function PaymentActions({ onCancel, onCheck, checking, onDashboard }) {
  return (
    <>
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-left">
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>Próximos passos:</strong> Após a confirmação, o vendedor será notificado para enviar o produto.
        </p>
      </div>
      <div className="flex gap-3 justify-center flex-wrap pt-2">
        <Button variant="outline" onClick={onCancel} className="rounded-xl gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="cancel-purchase-btn">
          Cancelar compra
        </Button>
        <Button variant="outline" onClick={onCheck} disabled={checking} className="rounded-xl gap-1.5" data-testid="check-payment-btn">
          {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Verificar pagamento
        </Button>
        <Button variant="outline" onClick={onDashboard} className="rounded-xl">Dashboard</Button>
      </div>
    </>
  );
}
